import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { AdminLayout, inp, lbl } from "@/pages/admin/adminShared";
import { formatDateTime } from "@/lib/format";
import { CheckCircle2, AlertTriangle, Ban, HelpCircle, Users, Copy, PhoneOff, Database, Save, History, Settings, ShieldAlert } from "lucide-react";

const STAT = [
  { key: "safe_to_send", label: "Safe to send", tone: "good" },
  { key: "matched", label: "Matched (both systems)", tone: "neutral" },
  { key: "website_only", label: "Website only", tone: "warn" },
  { key: "clicksend_only", label: "ClickSend only", tone: "warn" },
  { key: "website_duplicates", label: "Website duplicates", tone: "warn" },
  { key: "clicksend_duplicates", label: "ClickSend duplicates", tone: "warn" },
  { key: "not_opted_in", label: "Not opted in", tone: "warn" },
  { key: "invalid_or_missing", label: "Invalid / missing mobile", tone: "bad" },
  { key: "suppressed", label: "Suppressed / opted out", tone: "bad" },
  { key: "suppression_unknown", label: "Suppression unknown", tone: "bad" },
];

const TONE = {
  good: "border-[#CDE5D7] bg-[#F0F7F2] text-[#4A6B5D]",
  neutral: "border-border bg-card text-[#2D1E18]",
  warn: "border-[#F3C9A8] bg-[#FEF6EC] text-[#9A5B1E]",
  bad: "border-[#F3B3A3] bg-[#FBE9E4] text-[#B04B26]",
};

const SNAP_COLS = ["matched", "website_only", "clicksend_only", "website_duplicates", "clicksend_duplicates", "not_opted_in", "invalid_or_missing", "suppressed", "suppression_unknown"];
const SNAP_LABEL = { matched: "Matched", website_only: "Web-only", clicksend_only: "CS-only", website_duplicates: "Web dupes", clicksend_duplicates: "CS dupes", not_opted_in: "Not opted", invalid_or_missing: "Invalid", suppressed: "Suppressed", suppression_unknown: "Unknown" };

function Section({ icon: Icon, title, items, render, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <h3 className="mb-3 flex items-center gap-2 font-display text-base font-extrabold text-[#2D1E18]"><Icon size={16} /> {title} <span className="text-sm font-semibold text-[#6E5B51]">({items.length})</span></h3>
      {items.length === 0 ? <p className="text-sm text-[#6E5B51]">None.</p> : (
        <ul className="space-y-1.5 text-sm">{items.map((it, i) => <li key={i} className="flex flex-wrap items-baseline gap-x-2">{render(it)}</li>)}</ul>
      )}
    </div>
  );
}

export default function ClickSendReconciliation() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["sms-reconciliation"], queryFn: async () => (await api.get("/admin/sms/reconciliation")).data });
  const { data: snapshots } = useQuery({ queryKey: ["recon-snapshots"], queryFn: async () => (await api.get("/admin/sms/reconciliation/snapshots")).data });
  const { data: status } = useQuery({ queryKey: ["connector-status"], queryFn: async () => (await api.get("/admin/sms/connector-status")).data });
  const { data: cfg } = useQuery({ queryKey: ["clicksend-config"], queryFn: async () => (await api.get("/admin/sms/clicksend-config")).data });

  const [optIds, setOptIds] = useState(null);
  const [ruleIds, setRuleIds] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [gate, setGate] = useState(null);

  const optVal = optIds ?? (cfg?.opt_out_list_ids || []).join(", ");
  const ruleVal = ruleIds ?? (cfg?.inbound_rule_dest_ids || []).join(", ");
  const confVal = confirmed ?? !!cfg?.confirmed;

  const saveConfig = async () => {
    try {
      const body = {
        opt_out_list_ids: optVal.split(",").map((s) => s.trim()).filter(Boolean),
        inbound_rule_dest_ids: ruleVal.split(",").map((s) => s.trim()).filter(Boolean),
        confirmed: confVal,
      };
      await api.put("/admin/sms/clicksend-config", body);
      qc.invalidateQueries({ queryKey: ["clicksend-config"] });
      setOptIds(null); setRuleIds(null); setConfirmed(null);
      toast.success("Opt-out source configuration saved.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const runGate = async () => {
    try { const { data } = await api.post("/admin/sms/pre-broadcast-check"); setGate(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveSnapshot = async () => {
    try { await api.post("/admin/sms/reconciliation/snapshot"); qc.invalidateQueries({ queryKey: ["recon-snapshots"] }); toast.success("Aggregate summary saved to history (auto-deletes after 30 days)."); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (isLoading) return <AdminLayout title="SMS Reconciliation"><Spinner /></AdminLayout>;
  const c = data.categories;
  const mono = "font-mono text-[#5C4A42]";

  return (
    <AdminLayout title="SMS Reconciliation" subtitle="Read-only comparison of website members against ClickSend contacts."
      actions={<button onClick={saveSnapshot} data-testid="recon-save-snapshot" className="inline-flex items-center gap-1.5 rounded-full bg-[#C85A32] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#B04B26]"><Save size={15} /> Save aggregate snapshot</button>}>
      <div className="mb-4 flex flex-wrap items-start gap-2 rounded-2xl border border-[#F3C9A8] bg-[#FEF6EC] px-4 py-3 text-sm text-[#9A5B1E]" data-testid="recon-sim-banner">
        <Database size={18} className="mt-0.5" />
        <div>
          <p><span className="mr-2 rounded-full bg-[#9A5B1E] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white" data-testid="recon-mode-badge">Simulated data</span>{data.note} Generated {formatDateTime(data.generated_at)}.</p>
          <p className="mt-1 text-xs" data-testid="recon-connector-status">Live ClickSend connector: <span className="font-bold">{status?.recon_enabled ? "ENABLED" : "disabled"}</span> · SMS sending: <span className="font-bold">{status?.sms_live_enabled ? "ENABLED" : "disabled"}</span>. This page shows simulated data only — no live ClickSend data is displayed.</p>
        </div>
      </div>

      {(() => {
        const f = data.freshness || {};
        const map = {
          current: { cls: TONE.good, Icon: CheckCircle2, label: "Current" },
          outdated: { cls: TONE.warn, Icon: AlertTriangle, label: "Outdated" },
          unavailable: { cls: TONE.bad, Icon: Ban, label: "Unavailable" },
        };
        const m = map[f.status] || map.unavailable;
        const FIcon = m.Icon;
        return (
          <div className={`mb-6 rounded-2xl border p-4 ${m.cls}`} data-testid="recon-freshness">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FIcon size={20} />
                <div>
                  <p className="text-sm font-extrabold">Suppression freshness: {m.label}</p>
                  <p className="text-xs">Last successful check: {f.last_successful_check ? formatDateTime(f.last_successful_check) : "never"}{f.age_hours != null ? ` · ${f.age_hours}h ago (max ${f.max_age_hours}h)` : ""}</p>
                </div>
              </div>
              <span className="rounded-full bg-[#FBE9E4] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-[#B04B26]" data-testid="recon-verified-flag">
                Not verified for live sending
              </span>
            </div>
            <p className="mt-2 text-xs">{f.fail_closed_note}</p>
          </div>
        );
      })()}

      <div className={`mb-6 rounded-2xl border p-4 text-sm ${data.suppression.available ? "border-border bg-card text-[#5C4A42]" : TONE.bad}`} data-testid="recon-suppression">
        <p className="font-bold text-[#2D1E18]">Suppression / opt-out data</p>
        <p className="mt-1">Status: {data.suppression.available ? "Available" : "Unavailable"} · Last synced: {data.suppression.synced_at ? formatDateTime(data.suppression.synced_at) : "unknown"}</p>
        <p className="mt-1 text-xs">{data.suppression.status_note}</p>
      </div>

      {isAdmin && (
        <div className="mb-6 rounded-2xl border border-border bg-card p-4" data-testid="recon-config">
          <h3 className="mb-1 flex items-center gap-2 font-display text-base font-extrabold text-[#2D1E18]"><Settings size={16} /> Opt-out source configuration</h3>
          <p className="mb-3 text-xs text-[#6E5B51]">Explicitly confirm which ClickSend lists and inbound-rule destinations hold opt-outs. Suppression is never derived from list names alone. If a newly-created source is later detected that isn't confirmed here, verification fails closed until you re-confirm.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className={lbl}>Opt-out list IDs (comma-separated)</label><input className={inp} value={optVal} onChange={(e) => setOptIds(e.target.value)} placeholder="e.g. 9, 11" data-testid="config-optout-ids" /></div>
            <div><label className={lbl}>Inbound-rule destination list IDs</label><input className={inp} value={ruleVal} onChange={(e) => setRuleIds(e.target.value)} placeholder="e.g. 10" data-testid="config-rule-ids" /></div>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-[#2D1E18]"><input type="checkbox" checked={confVal} onChange={(e) => setConfirmed(e.target.checked)} data-testid="config-confirmed" /> I confirm these are all current opt-out/suppression sources</label>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={saveConfig} data-testid="config-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#C85A32] px-4 py-2 text-sm font-bold text-white hover:bg-[#B04B26]"><Save size={14} /> Save configuration</button>
            {cfg?.updated_at && <span className="text-xs text-[#6E5B51]">Last updated by {cfg.updated_by} · {formatDateTime(cfg.updated_at)}</span>}
          </div>
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-[#F3B3A3] bg-[#FBE9E4] p-4" data-testid="recon-gate">
        <h3 className="mb-1 flex items-center gap-2 font-display text-base font-extrabold text-[#B04B26]"><ShieldAlert size={16} /> Pre-broadcast safety gate (design preview)</h3>
        <p className="mb-3 text-xs text-[#9A5B1E]">Before any future live broadcast, a mandatory fresh suppression check must pass. Sending is blocked if ClickSend is unreachable, any opt-out list can't be retrieved, suppression is incomplete, consent was withdrawn, a number is invalid, or the recipient is suppressed.</p>
        <button onClick={runGate} data-testid="gate-run" className="inline-flex items-center gap-1.5 rounded-full border border-[#C85A32] bg-white px-4 py-2 text-sm font-bold text-[#B04B26]">Run pre-broadcast check</button>
        {gate && (
          <div className="mt-3 text-sm" data-testid="gate-result">
            <p className={`font-extrabold ${gate.overall_allowed ? "text-[#4A6B5D]" : "text-[#B04B26]"}`}>{gate.overall_allowed ? "ALLOWED" : "BLOCKED (fail closed)"}</p>
            {gate.blocked_reason && <p className="mt-1 text-xs text-[#9A5B1E]">{gate.blocked_reason}</p>}
            <p className="mt-1 text-xs text-[#5C4A42]">SMS sending master switch: <span className="font-bold">{gate.sms_live_enabled ? "ENABLED" : "disabled"}</span></p>
          </div>
        )}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {STAT.map((s) => (
          <div key={s.key} className={`rounded-2xl border p-3 ${TONE[s.tone]}`} data-testid={`recon-stat-${s.key}`}>
            <p className="text-2xl font-extrabold">{data.summary[s.key]}</p>
            <p className="text-xs font-semibold">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section testid="recon-safe" icon={CheckCircle2} title="Safe to send" items={c.safe_to_send} render={(it) => <><span className="font-semibold text-[#2D1E18]">{it.name}</span><span className={mono}>{it.masked}</span></>} />
        <Section testid="recon-suppressed" icon={Ban} title="Suppressed / opted out (do not send)" items={c.suppressed} render={(it) => <><span className="font-semibold text-[#2D1E18]">{it.name}</span><span className={mono}>{it.masked}</span><span className="text-xs text-[#B04B26]">{it.reason}</span></>} />
        <Section testid="recon-suppression-unknown" icon={HelpCircle} title="Suppression unknown / outdated (not safe)" items={c.suppression_unknown} render={(it) => <><span className="font-semibold text-[#2D1E18]">{it.name}</span><span className={mono}>{it.masked}</span><span className="text-xs text-[#9A5B1E]">{it.reason}</span></>} />
        <Section testid="recon-invalid" icon={PhoneOff} title="Invalid / missing mobile" items={c.invalid_or_missing} render={(it) => <><span className="font-semibold text-[#2D1E18]">{it.name}</span><span className={mono}>{it.masked}</span></>} />
        <Section testid="recon-not-opted" icon={AlertTriangle} title="Not opted in to SMS" items={c.not_opted_in} render={(it) => <><span className="font-semibold text-[#2D1E18]">{it.name}</span><span className={mono}>{it.masked}</span></>} />
        <Section testid="recon-matched" icon={Users} title="Matched (both systems)" items={c.matched} render={(it) => <><span className="font-semibold text-[#2D1E18]">{it.name}</span><span className={mono}>{it.masked}</span><span className="text-xs text-[#6E5B51]">{it.status}</span></>} />
        <Section testid="recon-website-only" icon={Users} title="Website only" items={c.website_only} render={(it) => <><span className="font-semibold text-[#2D1E18]">{it.name}</span><span className={mono}>{it.masked}</span></>} />
        <Section testid="recon-clicksend-only" icon={Users} title="ClickSend only" items={c.clicksend_only} render={(it) => <><span className={mono}>{it.masked}</span><span className="text-xs text-[#6E5B51]">{(it.lists || []).join(", ")}</span></>} />
        <Section testid="recon-web-dupes" icon={Copy} title="Website duplicate numbers" items={c.website_duplicates} render={(it) => <><span className={mono}>{it.masked}</span><span className="text-xs text-[#6E5B51]">{it.names.join(", ")} (×{it.count})</span></>} />
        <Section testid="recon-cs-dupes" icon={Copy} title="ClickSend duplicate numbers" items={c.clicksend_duplicates} render={(it) => <><span className={mono}>{it.masked}</span><span className="text-xs text-[#6E5B51]">{(it.lists || []).join(", ")} (×{it.count})</span></>} />
      </div>

      <div className="mt-8" data-testid="recon-history">
        <h2 className="mb-2 flex items-center gap-2 font-display text-lg font-extrabold text-[#2D1E18]"><History size={18} /> Aggregate history</h2>
        <p className="mb-3 text-xs text-[#6E5B51]">Non-identifying counts only — no phone numbers, names or member IDs are stored. Entries auto-delete 30 days after they are saved.</p>
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-[#6E5B51]"><tr>
              <th className="p-2.5">Saved</th><th className="p-2.5">By</th>
              {SNAP_COLS.map((k) => <th key={k} className="p-2.5 text-center">{SNAP_LABEL[k]}</th>)}
              <th className="p-2.5">Freshness</th>
            </tr></thead>
            <tbody>
              {(snapshots || []).map((s) => (
                <tr key={s.id} className="border-b border-border last:border-0" data-testid={`snap-row-${s.id}`}>
                  <td className="p-2.5 text-[#5C4A42]">{formatDateTime(s.created_at)}</td>
                  <td className="p-2.5 text-[#2D1E18]">{s.created_by_name}</td>
                  {SNAP_COLS.map((k) => <td key={k} className="p-2.5 text-center font-semibold text-[#2D1E18]">{s.summary?.[k] ?? 0}</td>)}
                  <td className="p-2.5 capitalize text-[#5C4A42]">{s.suppression_status}</td>
                </tr>
              ))}
              {(!snapshots || snapshots.length === 0) && <tr><td colSpan={SNAP_COLS.length + 3} className="p-5 text-center text-sm text-[#6E5B51]" data-testid="snap-empty">No saved snapshots yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
